import {
  CheckCircleOutlined, EditOutlined, KeyOutlined, MoreOutlined, PlusOutlined, StopOutlined, TeamOutlined
} from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PermissionCode, SystemRole, type StaffUserSummary } from "@autoflow/types";
import {
  Alert, Button, Card, Dropdown, Form, Input, Modal, Result, Select, Space, Table, Tag, Typography, message
} from "antd";
import { useState } from "react";
import { useAuth } from "../auth/AuthProvider";
import { useAppConfig } from "../../shared/config/AppConfigProvider";
import { formatDateTime, formatPhone, formatPhoneInput } from "../../shared/utils/formatters";
import {
  usersApi, type CreateStaffUserInput, type ManageableStaffRole
} from "./users-api";

type ProfileValues = { displayName: string; email: string; phone?: string };
type RoleValues = { role: ManageableStaffRole };
type PasswordValues = { temporaryPassword: string; confirmPassword: string };

const roleValues: ManageableStaffRole[] = [
  SystemRole.Admin,
  SystemRole.Manager,
  SystemRole.Advisor,
  SystemRole.Technician
];

export function UsersPage() {
  const { text } = useAppConfig();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [createOpen,setCreateOpen] = useState(false);
  const [profileTarget,setProfileTarget] = useState<StaffUserSummary|null>(null);
  const [roleTarget,setRoleTarget] = useState<StaffUserSummary|null>(null);
  const [passwordTarget,setPasswordTarget] = useState<StaffUserSummary|null>(null);
  const [statusTarget,setStatusTarget] = useState<StaffUserSummary|null>(null);
  const [createForm] = Form.useForm<CreateStaffUserInput>();
  const [profileForm] = Form.useForm<ProfileValues>();
  const [roleForm] = Form.useForm<RoleValues>();
  const [passwordForm] = Form.useForm<PasswordValues>();
  const isAdmin = Boolean(user?.roles.includes(SystemRole.Admin) && user.permissions.includes(PermissionCode.UserManage));
  const users = useQuery({queryKey:["users"],queryFn:usersApi.list,enabled:isAdmin});
  const refresh = () => queryClient.invalidateQueries({queryKey:["users"]});
  const success = async (label:string) => { await refresh(); message.success(label); };
  const createUser = useMutation({
    mutationFn:usersApi.create,
    onSuccess:async()=>{await success(text.personAdded);setCreateOpen(false);createForm.resetFields();}
  });
  const updateProfile = useMutation({
    mutationFn:({id,input}:{id:string;input:ProfileValues})=>usersApi.updateProfile(id,input),
    onSuccess:async()=>{await success(text.personUpdated);setProfileTarget(null);}
  });
  const updateRole = useMutation({
    mutationFn:({id,role}:{id:string;role:ManageableStaffRole})=>usersApi.updateRole(id,role),
    onSuccess:async()=>{await success(text.roleAssignmentUpdated);setRoleTarget(null);}
  });
  const resetPassword = useMutation({
    mutationFn:({id,temporaryPassword}:{id:string;temporaryPassword:string})=>usersApi.resetPassword(id,temporaryPassword),
    onSuccess:async()=>{await success(text.temporaryPasswordReset);setPasswordTarget(null);passwordForm.resetFields();}
  });
  const updateStatus = useMutation({
    mutationFn:({id,isActive}:{id:string;isActive:boolean})=>usersApi.updateStatus(id,isActive),
    onSuccess:async()=>{await success(text.personStatusUpdated);setStatusTarget(null);}
  });

  if (!isAdmin) return <Result status="403" title="403" subTitle={text.accessDenied}/>;

  const roleLabel = (role:string) => role===SystemRole.Admin?text.staffRoleAdmin
    :role===SystemRole.Manager?text.staffRoleManager
    :role===SystemRole.Advisor?text.staffRoleFrontDesk
    :role===SystemRole.Technician?text.staffRoleTechnician:role;
  const roleOptions = roleValues.map(value=>({value,label:roleLabel(value)}));
  const openCreate = () => {
    createUser.reset();
    createForm.setFieldsValue({displayName:"",email:"",phone:"",role:SystemRole.Advisor,temporaryPassword:"",isActive:true});
    setCreateOpen(true);
  };
  const openProfile = (target:StaffUserSummary) => {
    updateProfile.reset();
    profileForm.setFieldsValue({displayName:target.displayName,email:target.email,phone:target.phone??""});
    setProfileTarget(target);
  };
  const openRole = (target:StaffUserSummary) => {
    updateRole.reset();
    const current = target.roles.find(item=>roleValues.includes(item.name as ManageableStaffRole))?.name as ManageableStaffRole|undefined;
    roleForm.setFieldsValue({role:current??SystemRole.Advisor});
    setRoleTarget(target);
  };
  const openPassword = (target:StaffUserSummary) => {
    resetPassword.reset();
    passwordForm.resetFields();
    setPasswordTarget(target);
  };
  const error = createUser.error||updateProfile.error||updateRole.error||resetPassword.error||updateStatus.error;
  const columns = [
    {title:text.name,dataIndex:"displayName",key:"name"},
    {title:text.email,dataIndex:"email",key:"email"},
    {title:text.phone,dataIndex:"phone",key:"phone",render:(value:string|null)=>formatPhone(value)},
    {title:text.roles,key:"roles",render:(_:unknown,row:StaffUserSummary)=><Space wrap>{row.roles.map(role=><Tag key={role.id} color="blue">{roleLabel(role.name)}</Tag>)}</Space>},
    {title:text.status,key:"active",render:(_:unknown,row:StaffUserSummary)=><Space wrap><Tag color={row.isActive?"green":"default"}>{row.isActive?text.enabled:text.disabled}</Tag>{row.mustChangePassword&&<Tag color="orange">{text.passwordChangeRequired}</Tag>}</Space>},
    {title:text.created,dataIndex:"createdAt",key:"created",render:formatDateTime},
    {title:text.action,key:"action",fixed:"right" as const,render:(_:unknown,row:StaffUserSummary)=><Dropdown trigger={["click"]} menu={{items:[
      {key:"profile",icon:<EditOutlined/>,label:text.editProfile},
      {key:"role",icon:<TeamOutlined/>,label:text.changeRole},
      {key:"password",icon:<KeyOutlined/>,label:text.resetPassword,disabled:row.id===user?.id},
      {type:"divider" as const},
      {key:"status",icon:row.isActive?<StopOutlined/>:<CheckCircleOutlined/>,label:row.isActive?text.disablePerson:text.enablePerson,danger:row.isActive,disabled:row.id===user?.id&&row.isActive}
    ],onClick:({key})=>{
      if(key==="profile") openProfile(row);
      if(key==="role") openRole(row);
      if(key==="password") openPassword(row);
      if(key==="status") setStatusTarget(row);
    }}}><Button type="text" icon={<MoreOutlined/>}>{text.action}</Button></Dropdown>}
  ];

  return <section>
    <div className="page-title"><div><Typography.Title level={2}>{text.userManagement}</Typography.Title><Typography.Paragraph type="secondary">{text.userManagementHint}</Typography.Paragraph></div><Button type="primary" icon={<PlusOutlined/>} onClick={openCreate}>{text.addPerson}</Button></div>
    {(users.isError||error)&&<Alert type="error" showIcon message={(users.error as Error)?.message||(error as Error)?.message} style={{marginBottom:16}}/>}
    <Card><Table<StaffUserSummary> rowKey="id" loading={users.isLoading} dataSource={users.data??[]} columns={columns} scroll={{x:1100}}/></Card>

    <Modal open={createOpen} title={text.addPerson} okText={text.addPerson} confirmLoading={createUser.isPending} onCancel={()=>setCreateOpen(false)} onOk={()=>void createForm.validateFields().then(values=>createUser.mutate(values))}>
      <Alert type="info" showIcon message={text.temporaryPasswordHint} style={{marginBottom:16}}/>
      <Form form={createForm} layout="vertical" preserve={false}>
        <Form.Item name="displayName" label={text.name} rules={[{required:true},{min:2},{max:100}]}><Input autoComplete="name"/></Form.Item>
        <Form.Item name="email" label={text.email} rules={[{required:true},{type:"email"},{max:254}]}><Input autoComplete="email"/></Form.Item>
        <Form.Item name="phone" label={text.phone} rules={[{pattern:/^\+?[0-9 ()-]{7,32}$|^$/}]} normalize={value=>formatPhoneInput(String(value??""))}><Input autoComplete="tel"/></Form.Item>
        <Form.Item name="role" label={text.roles} rules={[{required:true}]}><Select options={roleOptions}/></Form.Item>
        <Form.Item name="temporaryPassword" label={text.temporaryPassword} rules={[{required:true},{min:12},{max:128}]}><Input.Password autoComplete="new-password"/></Form.Item>
        <Form.Item name="isActive" label={text.status} rules={[{required:true}]}><Select options={[{value:true,label:text.enabled},{value:false,label:text.disabled}]}/></Form.Item>
      </Form>
    </Modal>

    <Modal open={Boolean(profileTarget)} title={text.editProfile} okText={text.save} confirmLoading={updateProfile.isPending} onCancel={()=>setProfileTarget(null)} onOk={()=>void profileForm.validateFields().then(input=>profileTarget&&updateProfile.mutate({id:profileTarget.id,input}))}>
      <Form form={profileForm} layout="vertical" preserve={false}>
        <Form.Item name="displayName" label={text.name} rules={[{required:true},{min:2},{max:100}]}><Input/></Form.Item>
        <Form.Item name="email" label={text.email} rules={[{required:true},{type:"email"},{max:254}]}><Input/></Form.Item>
        <Form.Item name="phone" label={text.phone} rules={[{pattern:/^\+?[0-9 ()-]{7,32}$|^$/}]} normalize={value=>formatPhoneInput(String(value??""))}><Input/></Form.Item>
      </Form>
    </Modal>

    <Modal open={Boolean(roleTarget)} title={text.changeRole} okText={text.save} confirmLoading={updateRole.isPending} onCancel={()=>setRoleTarget(null)} onOk={()=>void roleForm.validateFields().then(values=>roleTarget&&updateRole.mutate({id:roleTarget.id,role:values.role}))}>
      {roleTarget?.id===user?.id&&<Alert type="warning" showIcon message={text.selfAdminRoleProtected} style={{marginBottom:16}}/>}
      <Form form={roleForm} layout="vertical" preserve={false}><Form.Item name="role" label={text.roles} rules={[{required:true}]}><Select options={roleOptions.filter(option=>roleTarget?.id!==user?.id||option.value===SystemRole.Admin)}/></Form.Item></Form>
    </Modal>

    <Modal open={Boolean(passwordTarget)} title={text.resetPassword} okText={text.resetPassword} confirmLoading={resetPassword.isPending} onCancel={()=>setPasswordTarget(null)} onOk={()=>void passwordForm.validateFields().then(values=>passwordTarget&&resetPassword.mutate({id:passwordTarget.id,temporaryPassword:values.temporaryPassword}))}>
      <Alert type="warning" showIcon message={text.resetPasswordHint} style={{marginBottom:16}}/>
      <Form form={passwordForm} layout="vertical" preserve={false}>
        <Form.Item name="temporaryPassword" label={text.temporaryPassword} rules={[{required:true},{min:12},{max:128}]}><Input.Password autoComplete="new-password"/></Form.Item>
        <Form.Item name="confirmPassword" label={text.confirmNewPassword} dependencies={["temporaryPassword"]} rules={[{required:true},({getFieldValue})=>({validator:(_,value)=>!value||getFieldValue("temporaryPassword")===value?Promise.resolve():Promise.reject(new Error(text.passwordsDoNotMatch))})]}><Input.Password autoComplete="new-password"/></Form.Item>
      </Form>
    </Modal>

    <Modal open={Boolean(statusTarget)} title={statusTarget?.isActive?text.disablePerson:text.enablePerson} okText={statusTarget?.isActive?text.disablePerson:text.enablePerson} okButtonProps={{danger:Boolean(statusTarget?.isActive)}} confirmLoading={updateStatus.isPending} onCancel={()=>setStatusTarget(null)} onOk={()=>statusTarget&&updateStatus.mutate({id:statusTarget.id,isActive:!statusTarget.isActive})}>
      <Alert type={statusTarget?.isActive?"warning":"info"} showIcon message={statusTarget?.isActive?text.disablePersonConfirm:text.enablePersonConfirm}/>
    </Modal>
  </section>;
}
